import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import financeService from '../services/financeService';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, AlertCircle, 
  BarChart3, PieChart, Calendar, ArrowUpRight, ArrowDownRight,
  Download, FileSpreadsheet, FileText, Filter, X, TrendingUp as TrendUp,
  Settings, Info
} from 'lucide-react';

const FinancePayrollAnalysis = () => {
  const { user } = useAuth();
  const canView = hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:view');
  
  // Estados de filtros
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStartMonth, setSelectedStartMonth] = useState(1);
  const [selectedEndMonth, setSelectedEndMonth] = useState(12);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [activeTab, setActiveTab] = useState('analysis'); // 'analysis', 'comparison', 'forecast'

  // Buscar usuários para filtro
  const { data: usersData } = useQuery({
    queryKey: ['users-for-analysis'],
    queryFn: () => userService.getUsers({ limit: 200, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    return [];
  }, [usersData]);

  // Lista de roles únicos para filtro
  const roles = useMemo(() => {
    const roleSet = new Set(users.map(u => u.role).filter(Boolean));
    return Array.from(roleSet).sort();
  }, [users]);

  // Query para análise principal
  const { data: analysisData, isLoading } = useQuery({
    queryKey: ['payroll-analysis', selectedYear, selectedStartMonth, selectedEndMonth, selectedUserId, selectedRole],
    queryFn: () => financeService.getPayrollAnalysis({
      year: selectedYear,
      start_month: selectedStartMonth,
      end_month: selectedEndMonth,
      user_id: selectedUserId || undefined,
      role: selectedRole || undefined
    }),
    enabled: !!selectedYear && canView && activeTab === 'analysis',
    staleTime: 0,
  });

  // Query para comparativo ano a ano (dinâmico based no ano mais antigo)
  const { data: comparisonData, isLoading: isLoadingComparison } = useQuery({
    queryKey: ['payroll-year-comparison', selectedYear, oldestYear],
    queryFn: () => {
      const startYear = Math.max(oldestYear, selectedYear - 2);
      const years = [];
      for (let y = startYear; y <= selectedYear; y++) {
        years.push(y);
      }
      return financeService.getPayrollYearComparison({
        years: years.join(',')
      });
    },
    enabled: canView && activeTab === 'comparison' && !!oldestYear,
    staleTime: 0,
  });

  // Query para previsões
  const { data: forecastData, isLoading: isLoadingForecast } = useQuery({
    queryKey: ['payroll-forecast'],
    queryFn: () => financeService.getPayrollForecast({ months_ahead: 6 }),
    enabled: canView && activeTab === 'forecast',
    staleTime: 0,
  });

  // Query para buscar configurações de indicadores
  const { data: indicatorsData } = useQuery({
    queryKey: ['payroll-indicators'],
    queryFn: () => financeService.getPayrollIndicators(),
    enabled: canView && activeTab === 'analysis',
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Buscar thresholds dos indicadores (valores padrão se não configurados)
  const getIndicatorThreshold = (key, defaultValue) => {
    if (!indicatorsData?.data) return defaultValue;
    const indicator = indicatorsData.data.find(
      ind => ind.indicator_key === key && ind.is_active
    );
    return indicator ? Number(indicator.threshold_percentage) : defaultValue;
  };

  const overtimeThreshold = React.useMemo(() => getIndicatorThreshold('OVERTIME_THRESHOLD', 10.0), [indicatorsData]);
  const extraAdvancesThreshold = React.useMemo(() => getIndicatorThreshold('EXTRA_ADVANCES_THRESHOLD', 5.0), [indicatorsData]);
  const diverseExpensesThreshold = React.useMemo(() => getIndicatorThreshold('DIVERSE_EXPENSES_THRESHOLD', 8.0), [indicatorsData]);
  const varianceThreshold = React.useMemo(() => getIndicatorThreshold('VARIANCE_THRESHOLD', 15.0), [indicatorsData]);

  // Query para buscar o ano mais antigo com dados de payslip (para comparativo dinâmico)
  const { data: oldestYearData } = useQuery({
    queryKey: ['payroll-oldest-year'],
    queryFn: () => financeService.getPayrollOldestYear(),
    staleTime: 5 * 60 * 1000,
  });

  const oldestYear = useMemo(() => {
    if (oldestYearData?.data?.year) {
      return Number(oldestYearData.data.year);
    }
    return new Date().getFullYear() - 2;
  }, [oldestYearData]);

  // Filtrar usuários por role se selecionado
  const filteredUsers = useMemo(() => {
    if (!selectedRole) return users;
    return users.filter(u => u.role === selectedRole);
  }, [users, selectedRole]);

  // Função para exportar Excel
  const handleExportExcel = async () => {
    try {
      toast.loading('Gerando arquivo Excel...', { id: 'export-excel' });
      const response = await financeService.exportPayrollAnalysisExcel({
        year: selectedYear,
        start_month: selectedStartMonth,
        end_month: selectedEndMonth,
        user_id: selectedUserId || undefined,
        role: selectedRole || undefined
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analise-folha-${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Excel exportado com sucesso!', { id: 'export-excel' });
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar Excel', { id: 'export-excel' });
    }
  };

  // Função para exportar PDF
  const handleExportPDF = async () => {
    try {
      if (!analysisData?.data) {
        toast.error('Carregue os dados da análise primeiro');
        return;
      }

      toast.loading('Gerando PDF...', { id: 'export-pdf' });
      
      // Criar PDF usando jsPDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      let currentY = margin;

      // Título
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Análise de Folha de Pagamento', pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Ano: ${selectedYear} | Período: ${selectedStartMonth}/${selectedYear} a ${selectedEndMonth}/${selectedYear}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;

      const analysis = analysisData.data;

      // Resumo
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Resumo Geral', margin, currentY);
      currentY += 8;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Total Previsto: R$ ${analysis.summary.totalProjected.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin, currentY);
      currentY += 6;
      pdf.text(`Total Efetivo: R$ ${analysis.summary.totalActual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin, currentY);
      currentY += 6;
      pdf.text(`Diferença: R$ ${analysis.summary.totalDifference.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${analysis.summary.variancePercentage >= 0 ? '+' : ''}${analysis.summary.variancePercentage.toFixed(2)}%)`, margin, currentY);
      currentY += 10;

      // Breakdown
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Breakdown de Gastos', margin, currentY);
      currentY += 8;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Salário Base: R$ ${analysis.summary.breakdown.baseSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin, currentY);
      currentY += 6;
      pdf.text(`Horas Extras: R$ ${analysis.summary.breakdown.overtime.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin, currentY);
      currentY += 6;
      pdf.text(`Vales Extras: R$ ${analysis.summary.breakdown.extraAdvances.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin, currentY);
      currentY += 6;
      pdf.text(`Despesas Diversas: R$ ${analysis.summary.breakdown.diverseExpenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin, currentY);
      currentY += 10;

      // Comparativo Mensal
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Comparativo Mensal', margin, currentY);
      currentY += 8;

      pdf.setFontSize(9);
      pdf.text('Mês', margin, currentY);
      pdf.text('Previsto', margin + 50, currentY);
      pdf.text('Efetivo', margin + 90, currentY);
      pdf.text('Diferença', margin + 130, currentY);
      currentY += 6;

      analysis.monthlyProjections.forEach((proj, idx) => {
        const actual = analysis.monthlyActuals[idx];
        const diff = actual.amount - proj.amount;
        
        if (currentY > 270) {
          pdf.addPage();
          currentY = margin;
        }

        pdf.text(proj.monthName.substring(0, 3), margin, currentY);
        pdf.text(`R$ ${proj.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin + 50, currentY);
        pdf.text(`R$ ${actual.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin + 90, currentY);
        pdf.setTextColor(diff >= 0 ? 255 : 0, diff >= 0 ? 0 : 200, 0);
        pdf.text(`R$ ${diff.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, margin + 130, currentY);
        pdf.setTextColor(0, 0, 0);
        currentY += 5;
      });

      // Salvar PDF
      pdf.save(`analise-folha-${selectedYear}.pdf`);
      toast.success('PDF gerado com sucesso!', { id: 'export-pdf' });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF', { id: 'export-pdf' });
    }
  };

  if (!canView) {
    return <div className="p-6 text-red-600">Sem permissão para visualizar análise de folha.</div>;
  }

  const analysis = analysisData?.data || null;
  const comparison = comparisonData?.data || [];
  const forecast = forecastData?.data || null;

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Análise de Folha de Pagamento</h1>
          <p className="text-gray-600">Análise comparativa previsto vs efetivo</p>
        </div>
        <div className="flex items-center gap-2">
          {hasPermission(user?.role, 'financial:full') && (
            <Link
              to="/financeiro/folha/indicadores"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              title="Configurar parâmetros dos indicadores"
            >
              <Settings className="w-5 h-5" />
              Config. Indicadores
            </Link>
          )}
          <button
            onClick={handleExportExcel}
            disabled={!analysis}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet className="w-5 h-5" />
            Exportar Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={!analysis}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-5 h-5" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('analysis')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'analysis'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="h-4 w-4 inline mr-2" />
              Análise Detalhada
            </button>
            <button
              onClick={() => {
                setActiveTab('comparison');
              }}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'comparison'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendUp className="h-4 w-4 inline mr-2" />
              Comparativo Ano a Ano
            </button>
            <button
              onClick={() => setActiveTab('forecast')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'forecast'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingUp className="h-4 w-4 inline mr-2" />
              Previsões
            </button>
          </nav>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
            <select
              value={selectedYear}
              onChange={(e) => {
                const newYear = Number(e.target.value);
                setSelectedYear(newYear);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {(() => {
                const currentYear = new Date().getFullYear();
                const minYear = oldestYear;
                const years = [];
                
                if (activeTab === 'comparison') {
                  for (let y = minYear; y <= currentYear; y++) {
                    years.push(y);
                  }
                } else {
                  for (let i = 0; i < 5; i++) {
                    years.push(currentYear - i);
                  }
                }
                
                return years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ));
              })()}
            </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mês Inicial</label>
            <select
              value={selectedStartMonth}
              onChange={(e) => setSelectedStartMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mês Final</label>
            <select
              value={selectedEndMonth}
              onChange={(e) => setSelectedEndMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Departamento (Role)</label>
            <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setSelectedUserId(''); // Limpar seleção de funcionário
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {filteredUsers.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
            {selectedRole && filteredUsers.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Nenhum funcionário encontrado neste departamento
              </p>
            )}
            {selectedRole && filteredUsers.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {filteredUsers.length} funcionário(s) no departamento
              </p>
            )}
          </div>
        </div>

        {/* Botão limpar filtros */}
        {(selectedRole || selectedUserId || selectedStartMonth !== 1 || selectedEndMonth !== 12) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setSelectedRole('');
                setSelectedUserId('');
                setSelectedStartMonth(1);
                setSelectedEndMonth(12);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              <X className="w-4 h-4" />
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Conteúdo da Tab de Análise */}
      {activeTab === 'analysis' && (
        <>
          {isLoading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : !analysis ? (
            <div className="p-6 text-gray-600">Carregando análise...</div>
          ) : (
            <>
              {/* Cards de Resumo */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Previsto</p>
                      <p className="text-2xl font-bold text-blue-600">
                        R$ {analysis.summary.totalProjected.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Efetivo</p>
                      <p className="text-2xl font-bold text-green-600">
                        R$ {analysis.summary.totalActual.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className={`bg-white rounded-lg shadow p-6 border-l-4 ${analysis.summary.totalDifference >= 0 ? 'border-red-500' : 'border-green-500'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Diferença</p>
                      <p className={`text-2xl font-bold ${analysis.summary.totalDifference >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {analysis.summary.totalDifference >= 0 ? '+' : ''}
                        R$ {analysis.summary.totalDifference.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {analysis.summary.variancePercentage >= 0 ? '+' : ''}{analysis.summary.variancePercentage.toFixed(2)}%
                      </p>
                    </div>
                    {analysis.summary.totalDifference >= 0 ? (
                      <ArrowUpRight className="w-8 h-8 text-red-500" />
                    ) : (
                      <ArrowDownRight className="w-8 h-8 text-green-500" />
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Média Mensal</p>
                      <p className="text-2xl font-bold text-purple-600">
                        R$ {(analysis.summary.totalActual / (selectedEndMonth - selectedStartMonth + 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Gráfico de Comparação Mensal */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6" />
                  Comparativo Mensal - Previsto vs Efetivo
                </h2>
                
                {(() => {
                  // Verificar se deve mostrar por funcionário (role selecionado e userId vazio)
                  const showByEmployee = selectedRole && !selectedUserId;
                  
                  // Obter lista de funcionários do departamento
                  const departmentEmployees = showByEmployee 
                    ? (analysis.employeeDetails[0]?.employees || []).map(emp => ({
                        user_id: emp.user_id,
                        full_name: emp.full_name
                      }))
                    : [];
                  
                  // Cores para cada funcionário
                  const employeeColors = [
                    { light: 'bg-blue-400', dark: 'bg-blue-600', hover: 'bg-blue-500', text: 'text-blue-900', selected: 'bg-blue-600' },
                    { light: 'bg-purple-400', dark: 'bg-purple-600', hover: 'bg-purple-500', text: 'text-purple-900', selected: 'bg-purple-600' },
                    { light: 'bg-indigo-400', dark: 'bg-indigo-600', hover: 'bg-indigo-500', text: 'text-indigo-900', selected: 'bg-indigo-600' },
                    { light: 'bg-pink-400', dark: 'bg-pink-600', hover: 'bg-pink-500', text: 'text-pink-900', selected: 'bg-pink-600' },
                    { light: 'bg-teal-400', dark: 'bg-teal-600', hover: 'bg-teal-500', text: 'text-teal-900', selected: 'bg-teal-600' },
                    { light: 'bg-orange-400', dark: 'bg-orange-600', hover: 'bg-orange-500', text: 'text-orange-900', selected: 'bg-orange-600' },
                    { light: 'bg-cyan-400', dark: 'bg-cyan-600', hover: 'bg-cyan-500', text: 'text-cyan-900', selected: 'bg-cyan-600' },
                    { light: 'bg-amber-400', dark: 'bg-amber-600', hover: 'bg-amber-500', text: 'text-amber-900', selected: 'bg-amber-600' },
                  ];
                  
                  // Calcular valor máximo para escala
                  const allValues = analysis.monthlyProjections.map((proj, idx) => {
                    const actual = analysis.monthlyActuals[idx];
                    return Math.max(proj.amount, actual.amount);
                  });
                  const maxValue = Math.max(...allValues, 1);
                  const chartHeight = 400; // Altura fixa do gráfico em pixels
                  
                  return (
                    <div className="relative">
                      {/* Legenda de funcionários se mostrar por funcionário */}
                      {showByEmployee && departmentEmployees.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">Legenda por Funcionário:</span>
                          {departmentEmployees.map((emp, idx) => {
                            const color = employeeColors[idx % employeeColors.length];
                            return (
                              <div key={emp.user_id} className="flex items-center gap-2">
                                <div className={`w-4 h-4 rounded ${color.light}`}></div>
                                <span className="text-xs text-gray-600">{emp.full_name}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Eixo Y com valores */}
                      <div className="absolute left-0 top-0 bottom-12 w-16 flex flex-col justify-between pr-2">
                        {[5, 4, 3, 2, 1, 0].map(i => {
                          const value = (maxValue / 5) * i;
                          return (
                            <div key={i} className="text-xs text-gray-600 text-right">
                              R$ {(value / 1000).toFixed(1)}k
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Linhas de grade horizontais */}
                      <div className="absolute left-16 right-0 top-0 bottom-12">
                        {[0, 1, 2, 3, 4, 5].map(i => (
                          <div
                            key={i}
                            className="absolute left-0 right-0 border-t border-gray-200"
                            style={{ top: `${(i / 5) * 100}%` }}
                          />
                        ))}
                      </div>
                      
                      {/* Área do gráfico */}
                      <div className="ml-16 pr-4" style={{ height: `${chartHeight + 60}px` }}>
                        <div className="flex items-end justify-between gap-1 h-full">
                          {analysis.monthlyProjections.map((proj, idx) => {
                            const actual = analysis.monthlyActuals[idx];
                            const monthData = analysis.employeeDetails.find(m => m.month === proj.month);
                            const difference = actual.amount - proj.amount;
                            const projectedHeight = maxValue > 0 ? (proj.amount / maxValue) * chartHeight : 0;
                            const actualHeight = maxValue > 0 ? (actual.amount / maxValue) * chartHeight : 0;
                            const isSelected = selectedMonth === proj.month;
                            
                            // Se mostrar por funcionário, calcular alturas individuais
                            const employeeProjectedHeights = showByEmployee && monthData
                              ? departmentEmployees.map(emp => {
                                  const empData = monthData.employees.find(e => e.user_id === emp.user_id);
                                  const empProjected = empData?.projected || 0;
                                  return {
                                    user_id: emp.user_id,
                                    full_name: emp.full_name,
                                    amount: empProjected,
                                    height: maxValue > 0 ? (empProjected / maxValue) * chartHeight : 0
                                  };
                                })
                              : [];
                            
                            const employeeActualHeights = showByEmployee && monthData
                              ? departmentEmployees.map(emp => {
                                  const empData = monthData.employees.find(e => e.user_id === emp.user_id);
                                  const empActual = empData?.actual || 0;
                                  return {
                                    user_id: emp.user_id,
                                    full_name: emp.full_name,
                                    amount: empActual,
                                    height: maxValue > 0 ? (empActual / maxValue) * chartHeight : 0
                                  };
                                })
                              : [];

                            return (
                              <div
                                key={proj.month}
                                className="flex-1 flex flex-col items-center cursor-pointer group h-full"
                                onClick={() => setSelectedMonth(proj.month)}
                              >
                                {/* Container das barras lado a lado */}
                                <div className="w-full flex items-end justify-center gap-1 flex-1" style={{ height: `${chartHeight}px` }}>
                                {/* Barra Prevista */}
                                <div className="flex-1 relative flex items-end gap-0.5" style={{ maxWidth: '48%', height: '100%' }}>
                                  {showByEmployee && employeeProjectedHeights.length > 0 ? (
                                    // Barras lado a lado por funcionário
                                    employeeProjectedHeights.map((emp, empIdx) => {
                                      const color = employeeColors[empIdx % employeeColors.length];
                                      const getColorClasses = () => {
                                        if (isSelected) {
                                          return color.selected + ' ring-2 ring-offset-1';
                                        }
                                        // Classes completas para hover
                                        const hoverMap = {
                                          'bg-blue-400': 'group-hover:bg-blue-500',
                                          'bg-purple-400': 'group-hover:bg-purple-500',
                                          'bg-indigo-400': 'group-hover:bg-indigo-500',
                                          'bg-pink-400': 'group-hover:bg-pink-500',
                                          'bg-teal-400': 'group-hover:bg-teal-500',
                                          'bg-orange-400': 'group-hover:bg-orange-500',
                                          'bg-cyan-400': 'group-hover:bg-cyan-500',
                                          'bg-amber-400': 'group-hover:bg-amber-500',
                                        };
                                        return color.light + ' ' + (hoverMap[color.light] || '');
                                      };
                                      const barWidth = `${100 / employeeProjectedHeights.length}%`;
                                      return (
                                        <div
                                          key={emp.user_id}
                                          className="flex-1 relative h-full"
                                          style={{ maxWidth: barWidth }}
                                        >
                                          <div
                                            className={`absolute bottom-0 w-full rounded-t transition-all shadow-md ${getColorClasses()}`}
                                            style={{ 
                                              height: `${emp.height}px`,
                                              minHeight: emp.height > 0 ? '4px' : '0'
                                            }}
                                            title={`${emp.full_name} - Previsto: R$ ${emp.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                          >
                                            {/* Valor dentro da barra se houver espaço */}
                                            {emp.height > 25 && (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <span className={`text-[9px] font-bold ${
                                                  isSelected ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                  {(emp.amount / 1000).toFixed(1)}k
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          {/* Valor acima da barra se não couber dentro */}
                                          {emp.height <= 25 && emp.height > 0 && (
                                            <div className={`absolute text-[8px] font-bold whitespace-nowrap ${
                                              isSelected ? 'text-gray-700 font-extrabold' : 'text-gray-600'
                                            }`} style={{ bottom: `${emp.height + 2}px`, left: '50%', transform: 'translateX(-50%)' }}>
                                              {(emp.amount / 1000).toFixed(1)}k
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    // Barra única (comportamento original)
                                    <>
                                      <div
                                        className={`absolute bottom-0 w-full rounded-t transition-all ${
                                          isSelected 
                                            ? 'bg-blue-600 ring-4 ring-blue-700 ring-offset-2 shadow-xl' 
                                            : 'bg-blue-400 group-hover:bg-blue-500 shadow-md'
                                        }`}
                                        style={{ 
                                          height: `${projectedHeight}px`,
                                          minHeight: projectedHeight > 0 ? '4px' : '0'
                                        }}
                                        title={`Previsto: R$ ${proj.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                      >
                                        {/* Valor dentro da barra se houver espaço */}
                                        {projectedHeight > 25 && (
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <span className={`text-[10px] font-bold ${
                                              isSelected ? 'text-white' : 'text-blue-900'
                                            }`}>
                                              {(proj.amount / 1000).toFixed(1)}k
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      {/* Valor acima da barra se não couber dentro */}
                                      {projectedHeight <= 25 && projectedHeight > 0 && (
                                        <div className={`absolute text-[9px] font-bold whitespace-nowrap ${
                                          isSelected ? 'text-blue-700 font-extrabold' : 'text-blue-600'
                                        }`} style={{ bottom: `${projectedHeight + 4}px`, left: '50%', transform: 'translateX(-50%)' }}>
                                          {(proj.amount / 1000).toFixed(1)}k
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                                
                                {/* Barra Efetiva */}
                                <div className="flex-1 relative flex items-end gap-0.5" style={{ maxWidth: '48%', height: '100%' }}>
                                  {showByEmployee && employeeActualHeights.length > 0 ? (
                                    // Barras lado a lado por funcionário
                                    employeeActualHeights.map((emp, empIdx) => {
                                      const color = employeeColors[empIdx % employeeColors.length];
                                      const empData = monthData?.employees.find(e => e.user_id === emp.user_id);
                                      const isAboveProjected = empData && empData.actual > empData.projected;
                                      const getColorClasses = () => {
                                        if (isSelected) {
                                          return color.selected + ' ring-2 ring-offset-1';
                                        }
                                        // Classes completas para hover
                                        const hoverMap = {
                                          'bg-blue-400': 'group-hover:bg-blue-500',
                                          'bg-purple-400': 'group-hover:bg-purple-500',
                                          'bg-indigo-400': 'group-hover:bg-indigo-500',
                                          'bg-pink-400': 'group-hover:bg-pink-500',
                                          'bg-teal-400': 'group-hover:bg-teal-500',
                                          'bg-orange-400': 'group-hover:bg-orange-500',
                                          'bg-cyan-400': 'group-hover:bg-cyan-500',
                                          'bg-amber-400': 'group-hover:bg-amber-500',
                                        };
                                        return color.light + ' ' + (hoverMap[color.light] || '');
                                      };
                                      const barWidth = `${100 / employeeActualHeights.length}%`;
                                      // Determinar cor da borda baseado na comparação com o previsto
                                      const borderClass = isAboveProjected 
                                        ? (isSelected ? 'ring-2 ring-red-600 ring-offset-1' : 'ring-2 ring-red-500')
                                        : (isSelected ? 'ring-2 ring-green-600 ring-offset-1' : 'ring-2 ring-green-500');
                                      return (
                                        <div
                                          key={emp.user_id}
                                          className="flex-1 relative h-full"
                                          style={{ maxWidth: barWidth }}
                                        >
                                          <div
                                            className={`absolute bottom-0 w-full rounded-t transition-all shadow-md ${getColorClasses()} ${borderClass}`}
                                            style={{ 
                                              height: `${emp.height}px`,
                                              minHeight: emp.height > 0 ? '4px' : '0'
                                            }}
                                            title={`${emp.full_name} - Efetivo: R$ ${emp.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ${isAboveProjected ? '(Acima do previsto)' : '(Abaixo do previsto)'}`}
                                          >
                                            {/* Valor dentro da barra se houver espaço */}
                                            {emp.height > 25 && (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <span className={`text-[9px] font-bold ${
                                                  isSelected ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                  {(emp.amount / 1000).toFixed(1)}k
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          {/* Valor acima da barra se não couber dentro */}
                                          {emp.height <= 25 && emp.height > 0 && (
                                            <div className={`absolute text-[8px] font-bold whitespace-nowrap ${
                                              isSelected ? 'text-gray-700 font-extrabold' : 'text-gray-600'
                                            }`} style={{ bottom: `${emp.height + 2}px`, left: '50%', transform: 'translateX(-50%)' }}>
                                              {(emp.amount / 1000).toFixed(1)}k
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    // Barra única (comportamento original)
                                    <>
                                      <div
                                        className={`absolute bottom-0 w-full rounded-t transition-all ${
                                          actual.amount > proj.amount 
                                            ? (isSelected 
                                                ? 'bg-red-600 ring-4 ring-red-700 ring-offset-2 shadow-xl' 
                                                : 'bg-red-400 group-hover:bg-red-500 shadow-md')
                                            : (isSelected 
                                                ? 'bg-green-600 ring-4 ring-green-700 ring-offset-2 shadow-xl' 
                                                : 'bg-green-400 group-hover:bg-green-500 shadow-md')
                                        }`}
                                        style={{ 
                                          height: `${actualHeight}px`,
                                          minHeight: actualHeight > 0 ? '4px' : '0'
                                        }}
                                        title={`Efetivo: R$ ${actual.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                      >
                                        {/* Valor dentro da barra se houver espaço */}
                                        {actualHeight > 25 && (
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <span className={`text-[10px] font-bold ${
                                              actual.amount > proj.amount 
                                                ? (isSelected ? 'text-white' : 'text-red-900')
                                                : (isSelected ? 'text-white' : 'text-green-900')
                                            }`}>
                                              {(actual.amount / 1000).toFixed(1)}k
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      {/* Valor acima da barra se não couber dentro */}
                                      {actualHeight <= 25 && actualHeight > 0 && (
                                        <div className={`absolute text-[9px] font-bold whitespace-nowrap ${
                                          actual.amount > proj.amount 
                                            ? (isSelected ? 'text-red-700 font-extrabold' : 'text-red-600')
                                            : (isSelected ? 'text-green-700 font-extrabold' : 'text-green-600')
                                        }`} style={{ bottom: `${actualHeight + 4}px`, left: '50%', transform: 'translateX(-50%)' }}>
                                          {(actual.amount / 1000).toFixed(1)}k
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                                </div>
                                
                              {/* Label do mês */}
                              <div className={`text-xs font-medium mt-2 transition-all ${
                                isSelected 
                                  ? 'text-white font-bold scale-110 bg-blue-600 px-3 py-1.5 rounded-lg shadow-lg' 
                                  : 'text-gray-600'
                              }`}>
                                {proj.monthName.substring(0, 3).toUpperCase()}
                              </div>
                              
                              {/* Diferença */}
                              <div className={`text-[10px] font-semibold mt-1 ${
                                difference >= 0 
                                  ? (isSelected ? 'text-red-700 font-extrabold' : 'text-red-600')
                                  : (isSelected ? 'text-green-700 font-extrabold' : 'text-green-600')
                              }`}>
                                {difference >= 0 ? '↑' : '↓'} R$ {Math.abs(difference).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                      
                      {/* Eixo X */}
                      <div className="ml-16 mt-2 border-t border-gray-300 pt-2"></div>
                    </div>
                  );
                })()}
                
                {/* Legenda */}
                <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-blue-400 rounded shadow-sm"></div>
                    <span className="text-sm text-gray-700 font-medium">Previsto</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-green-400 rounded shadow-sm"></div>
                    <span className="text-sm text-gray-700 font-medium">Efetivo (Economia)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-red-400 rounded shadow-sm"></div>
                    <span className="text-sm text-gray-700 font-medium">Efetivo (Acima do Previsto)</span>
                  </div>
                </div>
                
                {/* Instrução */}
                <p className="text-xs text-gray-500 text-center mt-3">
                  💡 Clique em um mês para ver detalhamento completo
                </p>
              </div>

              {/* Breakdown de Gastos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Componente de Pizza Chart */}
                {(() => {
                  const PieChartComponent = ({ data, total, title }) => {
                    if (total === 0) {
                      return (
                        <div className="bg-white rounded-lg shadow p-6">
                          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <PieChart className="w-6 h-6" />
                            {title}
                          </h2>
                          <p className="text-gray-500 text-center py-8">Nenhum gasto registrado ainda</p>
                        </div>
                      );
                    }

                    const size = 200;
                    const radius = size / 2 - 10;
                    const centerX = size / 2;
                    const centerY = size / 2;
                    let currentAngle = -Math.PI / 2; // Começar do topo

                    const colors = {
                      'Salário Base': '#3b82f6', // blue-500
                      'Horas Extras': '#f97316', // orange-500
                      'Vales Extras': '#eab308', // yellow-500
                      'Despesas Diversas': '#a855f7' // purple-500
                    };

                    const paths = data.map((item) => {
                      const percentage = (item.value / total) * 100;
                      if (percentage === 0) return null;
                      
                      const angle = (item.value / total) * 2 * Math.PI;
                      const startAngle = currentAngle;
                      const endAngle = currentAngle + angle;
                      
                      // Caso especial: quando é 100% (círculo completo)
                      let pathData;
                      if (Math.abs(angle - 2 * Math.PI) < 0.001) {
                        // Círculo completo
                        pathData = `M ${centerX} ${centerY} m -${radius} 0 a ${radius} ${radius} 0 1 1 ${radius * 2} 0 a ${radius} ${radius} 0 1 1 -${radius * 2} 0`;
                      } else {
                        const x1 = centerX + radius * Math.cos(startAngle);
                        const y1 = centerY + radius * Math.sin(startAngle);
                        const x2 = centerX + radius * Math.cos(endAngle);
                        const y2 = centerY + radius * Math.sin(endAngle);
                        
                        const largeArcFlag = angle > Math.PI ? 1 : 0;
                        
                        pathData = [
                          `M ${centerX} ${centerY}`,
                          `L ${x1} ${y1}`,
                          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                          'Z'
                        ].join(' ');
                      }
                      
                      const path = { pathData, color: colors[item.label] || '#gray', label: item.label, value: item.value, percentage };
                      currentAngle = endAngle;
                      return path;
                    }).filter(Boolean);

                    return (
                      <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <PieChart className="w-6 h-6" />
                          {title}
                        </h2>
                        <div className="flex flex-col md:flex-row gap-6 items-center">
                          {/* Gráfico de Pizza */}
                          <div className="flex-shrink-0">
                            <svg width={size} height={size} className="drop-shadow-lg">
                              {paths.map((path, idx) => {
                                // Se for 100%, usar círculo completo
                                if (Math.abs(path.percentage - 100) < 0.1) {
                                  return (
                                    <circle
                                      key={idx}
                                      cx={centerX}
                                      cy={centerY}
                                      r={radius}
                                      fill={path.color}
                                      stroke="white"
                                      strokeWidth="2"
                                      className="hover:opacity-80 transition-opacity cursor-pointer"
                                      title={`${path.label}: ${path.percentage.toFixed(1)}%`}
                                    />
                                  );
                                }
                                return (
                                  <path
                                    key={idx}
                                    d={path.pathData}
                                    fill={path.color}
                                    stroke="white"
                                    strokeWidth="2"
                                    className="hover:opacity-80 transition-opacity cursor-pointer"
                                    title={`${path.label}: ${path.percentage.toFixed(1)}%`}
                                  />
                                );
                              })}
                            </svg>
                          </div>
                          
                          {/* Legenda */}
                          <div className="flex-1 space-y-3">
                            {data.map((item) => {
                              const percentage = (item.value / total) * 100;
                              const color = colors[item.label] || '#gray';
                              return (
                                <div key={item.label} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-4 h-4 rounded"
                                      style={{ backgroundColor: color }}
                                    ></div>
                                    <span className="text-sm text-gray-600">{item.label}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium w-24 text-right">
                                      R$ {item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                    <span className="text-xs text-gray-500 w-12 text-right">
                                      ({percentage.toFixed(1)}%)
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  };

                  // Calcular breakdown previsto (baseado em projeções)
                  // No previsto, apenas salário base é considerado (horas extras, vales e despesas são imprevistos)
                  const projectedBreakdown = {
                    baseSalary: analysis.summary.totalProjected,
                    overtime: 0,
                    extraAdvances: 0,
                    diverseExpenses: 0
                  };

                  const actualData = [
                    { label: 'Salário Base', value: analysis.summary.breakdown.baseSalary },
                    { label: 'Horas Extras', value: analysis.summary.breakdown.overtime },
                    { label: 'Vales Extras', value: analysis.summary.breakdown.extraAdvances },
                    { label: 'Despesas Diversas', value: analysis.summary.breakdown.diverseExpenses }
                  ];

                  const projectedData = [
                    { label: 'Salário Base', value: projectedBreakdown.baseSalary },
                    { label: 'Horas Extras', value: projectedBreakdown.overtime },
                    { label: 'Vales Extras', value: projectedBreakdown.extraAdvances },
                    { label: 'Despesas Diversas', value: projectedBreakdown.diverseExpenses }
                  ].filter(item => item.value > 0); // Filtrar valores zero

                  const actualDataFiltered = actualData.filter(item => item.value > 0); // Filtrar valores zero

                  return (
                    <>
                      <PieChartComponent 
                        data={projectedData.length > 0 ? projectedData : [{ label: 'Salário Base', value: analysis.summary.totalProjected }]} 
                        total={analysis.summary.totalProjected} 
                        title="Distribuição de Gastos Previstos"
                      />
                      <PieChartComponent 
                        data={actualDataFiltered.length > 0 ? actualDataFiltered : actualData} 
                        total={analysis.summary.totalActual} 
                        title="Distribuição de Gastos Efetivos"
                      />
                    </>
                  );
                })()}

                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-6 h-6" />
                    Indicadores e Alertas
                  </h2>
                  <div className="space-y-4">
                    {analysis.summary.breakdown.overtime > 0 && (
                      <div className={`p-4 rounded-lg ${analysis.summary.breakdown.overtime > analysis.summary.totalActual * (overtimeThreshold / 100) ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">Horas Extras</span>
                          <span className={`font-bold ${analysis.summary.breakdown.overtime > analysis.summary.totalActual * (overtimeThreshold / 100) ? 'text-red-600' : 'text-yellow-600'}`}>
                            {((analysis.summary.breakdown.overtime / analysis.summary.totalActual) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {analysis.summary.breakdown.overtime > analysis.summary.totalActual * (overtimeThreshold / 100)
                            ? `⚠️ Horas extras acima de ${overtimeThreshold.toFixed(2)}% do total - Revisar necessidade`
                            : `Horas extras dentro do esperado (limite: ${overtimeThreshold.toFixed(2)}%)`}
                        </p>
                      </div>
                    )}

                    {analysis.summary.breakdown.extraAdvances > 0 && (
                      <div className={`p-4 rounded-lg ${
                        analysis.summary.breakdown.extraAdvances > analysis.summary.totalActual * (extraAdvancesThreshold / 100)
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-yellow-50 border border-yellow-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">Vales Extras</span>
                          <span className={`font-bold ${
                            analysis.summary.breakdown.extraAdvances > analysis.summary.totalActual * (extraAdvancesThreshold / 100)
                              ? 'text-red-600'
                              : 'text-yellow-600'
                          }`}>
                            {((analysis.summary.breakdown.extraAdvances / analysis.summary.totalActual) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {analysis.summary.breakdown.extraAdvances > analysis.summary.totalActual * (extraAdvancesThreshold / 100)
                            ? `⚠️ Vales extras acima de ${extraAdvancesThreshold.toFixed(2)}% do total - Revisar necessidade`
                            : `Vales extras dentro do esperado (limite: ${extraAdvancesThreshold.toFixed(2)}%)`}
                        </p>
                      </div>
                    )}

                    {analysis.summary.breakdown.diverseExpenses > 0 && (
                      <div className={`p-4 rounded-lg ${
                        analysis.summary.breakdown.diverseExpenses > analysis.summary.totalActual * (diverseExpensesThreshold / 100)
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-purple-50 border border-purple-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">Despesas Diversas</span>
                          <span className={`font-bold ${
                            analysis.summary.breakdown.diverseExpenses > analysis.summary.totalActual * (diverseExpensesThreshold / 100)
                              ? 'text-red-600'
                              : 'text-purple-600'
                          }`}>
                            {((analysis.summary.breakdown.diverseExpenses / analysis.summary.totalActual) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {analysis.summary.breakdown.diverseExpenses > analysis.summary.totalActual * (diverseExpensesThreshold / 100)
                            ? `⚠️ Despesas diversas acima de ${diverseExpensesThreshold.toFixed(2)}% do total - Revisar necessidade`
                            : `Despesas diversas dentro do esperado (limite: ${diverseExpensesThreshold.toFixed(2)}%)`}
                        </p>
                      </div>
                    )}

                    {/* Indicador de Variação Previsto vs Efetivo */}
                    {analysis.summary.totalDifference > 0 && analysis.summary.totalProjected > 0 && (
                      <div className={`p-4 rounded-lg ${
                        analysis.summary.variancePercentage > varianceThreshold
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-blue-50 border border-blue-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">Variação Previsto vs Efetivo</span>
                          <span className={`font-bold ${
                            analysis.summary.variancePercentage > varianceThreshold
                              ? 'text-red-600'
                              : 'text-blue-600'
                          }`}>
                            +{analysis.summary.variancePercentage.toFixed(2)}%
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {analysis.summary.variancePercentage > varianceThreshold
                            ? `⚠️ Gastos efetivos ${analysis.summary.variancePercentage.toFixed(2)}% acima do previsto (limite: ${varianceThreshold.toFixed(2)}%) - Investigar causas`
                            : `Variação dentro do esperado (limite: ${varianceThreshold.toFixed(2)}%)`}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Diferença: R$ {analysis.summary.totalDifference.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Detalhamento por Mês */}
              {selectedMonth && (
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Calendar className="w-6 h-6" />
                      Detalhamento - {analysis.employeeDetails.find(m => m.month === selectedMonth)?.monthName}
                    </h2>
                    <button
                      onClick={() => setSelectedMonth(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modalidade</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Previsto</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Efetivo</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Diferença</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Breakdown</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {analysis.employeeDetails.find(m => m.month === selectedMonth)?.employees.map((emp) => (
                          <tr key={emp.user_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium">{emp.full_name}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                {emp.salary_modality === 'MONTHLY' ? 'Mensalista' : 'Horista'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-blue-600 font-medium">
                              R$ {emp.projected.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {emp.has_payslip ? (
                                <span className="text-green-600 font-medium">
                                  R$ {emp.actual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {emp.has_payslip ? (
                                <span className={emp.difference >= 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                                  {emp.difference >= 0 ? '+' : ''}
                                  R$ {emp.difference.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {emp.has_payslip ? (
                                <div className="flex flex-wrap gap-1">
                                  {emp.breakdown.overtime > 0 && (
                                    <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-800" title="Horas Extras">
                                      HE: R$ {emp.breakdown.overtime.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {emp.breakdown.extra_advances > 0 && (
                                    <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800" title="Vales Extras">
                                      VE: R$ {emp.breakdown.extra_advances.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {emp.breakdown.diverse_expenses > 0 && (
                                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-800" title="Despesas Diversas">
                                      DD: R$ {emp.breakdown.diverse_expenses.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {emp.breakdown.advance_deduction > 0 && (
                                    <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800" title="Adiantamento">
                                      AD: R$ {emp.breakdown.advance_deduction.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">Sem holerite aprovado</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Conteúdo da Tab Comparativo Ano a Ano */}
      {activeTab === 'comparison' && (
        <>
          {isLoadingComparison ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : comparison.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Nenhum dado disponível</h3>
                  <p className="text-sm text-blue-800">
                    Não há dados suficientes para gerar o comparativo ano a ano para os anos selecionados.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cards de Resumo por Ano */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {comparison.map((yearData) => (
                  <div key={yearData.year} className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{yearData.year}</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Previsto:</span>
                        <span className="text-sm font-medium text-blue-600">
                          R$ {yearData.totalProjected.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Efetivo:</span>
                        <span className="text-sm font-medium text-green-600">
                          R$ {yearData.totalActual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-sm font-medium text-gray-900">Diferença:</span>
                        <span className={`text-sm font-bold ${yearData.totalDifference >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {yearData.totalDifference >= 0 ? '+' : ''}
                          R$ {yearData.totalDifference.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Variação: {yearData.variancePercentage >= 0 ? '+' : ''}{yearData.variancePercentage.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Gráfico Comparativo */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Comparativo Mensal - Múltiplos Anos</h2>
                <div className="h-96 flex items-end justify-between gap-1">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                    const monthName = new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' });
                    const maxValue = Math.max(...comparison.flatMap(y => y.monthlyData.map(m => Math.max(m.projected, m.actual))));
                    
                    return (
                      <div key={month} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex flex-col items-end gap-0.5 relative" style={{ height: '300px' }}>
                          {comparison.map((yearData, yearIdx) => {
                            const monthData = yearData.monthlyData.find(m => m.month === month);
                            if (!monthData) return null;
                            
                            const projectedHeight = (monthData.projected / maxValue) * 100;
                            const actualHeight = (monthData.actual / maxValue) * 100;
                            const colors = ['bg-blue-400', 'bg-green-400', 'bg-purple-400'];
                            const yearColor = colors[yearIdx % colors.length];

                            return (
                              <React.Fragment key={yearData.year}>
                                <div
                                  className={`w-full ${yearColor} opacity-60 rounded-t`}
                                  style={{ height: `${projectedHeight}%` }}
                                  title={`${yearData.year} Previsto: R$ ${monthData.projected.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                />
                                <div
                                  className={`w-full ${monthData.actual > monthData.projected ? 'bg-red-400' : 'bg-green-400'} opacity-80 rounded-t`}
                                  style={{ height: `${actualHeight}%` }}
                                  title={`${yearData.year} Efetivo: R$ ${monthData.actual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                />
                              </React.Fragment>
                            );
                          })}
                        </div>
                        <div className="text-xs font-medium text-gray-600 mt-2">
                          {monthName.substring(0, 3)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
                  {comparison.map((yearData, idx) => {
                    const colors = ['bg-blue-400', 'bg-green-400', 'bg-purple-400'];
                    return (
                      <div key={yearData.year} className="flex items-center gap-2">
                        <div className={`w-4 h-4 ${colors[idx % colors.length]} rounded`}></div>
                        <span className="text-sm text-gray-600">{yearData.year}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Conteúdo da Tab Previsões */}
      {activeTab === 'forecast' && (
        <>
          {isLoadingForecast ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : !forecast ? (
            <div className="p-6 text-gray-600">Carregando previsões...</div>
          ) : forecast.historicalData.length < 3 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8">
              <div className="flex items-start gap-4">
                <Info className="w-8 h-8 text-yellow-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                    Dados Insuficientes para Previsões
                  </h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    A funcionalidade de previsões requer pelo menos <strong>3 meses de dados históricos</strong> de folha de pagamento para gerar estimativas confiáveis.
                  </p>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Dados disponíveis:</strong> {forecast.historicalData.length} {forecast.historicalData.length === 1 ? 'mês' : 'meses'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {forecast.historicalData.length === 0 
                        ? 'Nenhum dado histórico encontrado ainda.'
                        : forecast.historicalData.length === 1
                        ? 'Aguarde mais 2 meses para começar a ver previsões.'
                        : 'Aguarde mais 1 mês para começar a ver previsões.'}
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Dica:</strong> Enquanto isso, você pode usar a aba <strong>"Análise Detalhada"</strong> para acompanhar os gastos atuais e projetados baseados nas configurações de salário dos funcionários.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Resumo de Previsões */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Média Histórica</p>
                      <p className="text-2xl font-bold text-blue-600">
                        R$ {forecast.averageHistorical.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Tendência Mensal</p>
                      <p className={`text-2xl font-bold ${forecast.trend >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {forecast.trend >= 0 ? '+' : ''}
                        R$ {forecast.trend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">por mês</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Previsto (6 meses)</p>
                      <p className="text-2xl font-bold text-green-600">
                        R$ {forecast.forecast.reduce((sum, f) => sum + f.predicted, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </div>
              </div>

              {/* Gráfico de Previsões */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Previsões para os Próximos 6 Meses</h2>
                <div className="h-80 flex items-end justify-between gap-2">
                  {forecast.forecast.map((item) => {
                    const maxValue = Math.max(...forecast.forecast.map(f => f.predicted));
                    const height = (item.predicted / maxValue) * 100;
                    const confidenceColors = {
                      'ALTA': 'bg-green-400',
                      'MEDIA': 'bg-yellow-400',
                      'BAIXA': 'bg-orange-400'
                    };

                    return (
                      <div key={`${item.year}-${item.month}`} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full flex flex-col items-center gap-1 relative">
                          <div
                            className={`w-full ${confidenceColors[item.confidence]} rounded-t transition-all`}
                            style={{ height: `${height}%` }}
                            title={`${item.monthName}: R$ ${item.predicted.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (Confiança: ${item.confidence})`}
                          />
                        </div>
                        <div className="text-xs font-medium text-gray-600">
                          {item.monthName.substring(0, 3)}
                        </div>
                        <div className="text-xs text-gray-500">
                          R$ {item.predicted.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                          item.confidence === 'ALTA' ? 'bg-green-100 text-green-800' :
                          item.confidence === 'MEDIA' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {item.confidence}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-400 rounded"></div>
                    <span className="text-sm text-gray-600">Alta Confiança</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                    <span className="text-sm text-gray-600">Média Confiança</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-400 rounded"></div>
                    <span className="text-sm text-gray-600">Baixa Confiança</span>
                  </div>
                </div>
              </div>

              {/* Tabela de Previsões */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Detalhamento das Previsões</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mês</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Previsto</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Confiança</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {forecast.forecast.map((item) => (
                        <tr key={`${item.year}-${item.month}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{item.monthName} / {item.year}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-blue-600">
                            R$ {item.predicted.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              item.confidence === 'ALTA' ? 'bg-green-100 text-green-800' :
                              item.confidence === 'MEDIA' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {item.confidence}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-4 py-3 text-sm font-bold">Total</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-blue-600">
                          R$ {forecast.forecast.reduce((sum, f) => sum + f.predicted, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FinancePayrollAnalysis;
